/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  COOKIE / CLIENT-STORAGE INVENTORY
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *  WHY AN INVENTORY AND NOT JUST FOUR TOGGLES
 *  ------------------------------------------
 *  DPDP s.5 requires the notice to carry an ITEMISED description of the personal
 *  data and its specific purpose. A banner offering "Analytics / Functional /
 *  Marketing" without saying what is actually stored is not an itemised notice —
 *  it is a category picker. This file is the real inventory, built by auditing
 *  every `localStorage` write in the codebase, so the banner can name them.
 *
 *  IMPORTANT: this app sets NO HTTP cookies of its own. Everything below is
 *  `localStorage`. That does not exempt it — DPDP governs personal data however
 *  it is stored, and "cookies and similar technologies" is the standard framing.
 *  Two entries below hold personal data and are called out as such.
 *
 *  ⚠ Keep this in sync when adding any client-side storage. An entry missing
 *    here is an entry missing from the patient's notice.
 */

export type CookieCategory = 'essential' | 'functional' | 'analytics' | 'marketing';

export interface StoredItem {
  /** The literal storage key, so the notice can be verified against the code. */
  key: string;
  storage: 'localStorage' | 'sessionStorage' | 'cookie';
  category: CookieCategory;
  /** Plain-language name shown to the patient. */
  name: string;
  purpose: string;
  /** Does this hold personal data as defined by DPDP s.2(t)? */
  personalData: boolean;
  retention: string;
  /** Cleared when the user signs out? */
  clearedOnLogout: boolean;
}

export const COOKIE_CATEGORIES: Record<CookieCategory, {
  label: string;
  description: string;
  /** Essential cannot be refused; everything else defaults OFF. */
  alwaysOn: boolean;
}> = {
  essential: {
    label: 'Strictly necessary',
    description:
      'Needed for the site to work — signing you in, keeping you signed in, and remembering where you are in a form. These cannot be switched off.',
    alwaysOn: true,
  },
  functional: {
    label: 'Functional',
    description:
      'Remember your preferences, such as dark mode, your region and whether the sidebar is collapsed.',
    alwaysOn: false,
  },
  analytics: {
    label: 'Analytics and performance',
    description:
      'Help us understand how the site is used so we can improve it. Not currently in use — no analytics tool is installed.',
    alwaysOn: false,
  },
  marketing: {
    label: 'Marketing',
    description:
      'Would be used to show you information about our services elsewhere. Not currently in use — no marketing tags are installed.',
    alwaysOn: false,
  },
};

/**
 * The actual inventory, as of the August 2026 audit.
 *
 * Findings worth reading before changing anything here:
 *
 *  • `symbian_search_recents` stores raw search text typed by STAFF. Because
 *    staff search patients by name, this puts patient names into localStorage
 *    on what is often a shared clinical workstation, and it survived sign-out.
 *    It is classified `essential: false` and now cleared on logout.
 *
 *  • `persist:root` (redux-persist) holds `hasPaidBefore` and `paymentStatus` —
 *    information about a named patient's payment state. Personal data.
 *
 *  • `stepper_completed` is no longer written at all. It gated the whole consent
 *    flow from the client, so a user could skip consent by setting it by hand.
 *    The server's `onboarding_completed` is now the only gate. Sign-out still
 *    purges the key so existing browsers do not keep a stale copy.
 */
export const STORED_ITEMS: StoredItem[] = [
  // ── Essential ────────────────────────────────────────────────────────────
  {
    key: 'CognitoIdentityServiceProvider.*',
    storage: 'localStorage',
    category: 'essential',
    name: 'Sign-in session',
    purpose: 'Keeps you signed in securely between page loads. Set by AWS Cognito.',
    personalData: true,
    retention: 'Until you sign out or the session expires.',
    clearedOnLogout: true,
  },
  {
    key: 'persist:root',
    storage: 'localStorage',
    category: 'essential',
    name: 'Account status',
    purpose:
      'Remembers whether you have an active order and your payment status, so the site does not ask you to pay again on reload.',
    personalData: true,
    retention: 'Until you sign out.',
    clearedOnLogout: true,
  },
  {
    key: 'cookieConsent',
    storage: 'localStorage',
    category: 'essential',
    name: 'Your cookie choices',
    purpose:
      'Remembers what you chose here, so we do not ask again on every page. Required in order to honour your choice.',
    personalData: false,
    retention: '12 months, then we ask again.',
    clearedOnLogout: false,
  },
  {
    key: 'symbian_anon_id',
    storage: 'localStorage',
    category: 'essential',
    name: 'Anonymous consent reference',
    purpose:
      'A random identifier created before you sign in, so the cookie choice you make on the banner can be recorded and later attached to your account. It is not linked to you until you sign in.',
    // A pseudonymous identifier, not an identifier of a known person — but it
    // is declared as personal data because once linked at sign-in it becomes
    // traceable to the account.
    personalData: true,
    retention: 'Until you clear your browser data.',
    // Deliberately NOT cleared: it belongs to the browser, and clearing it
    // would orphan the cookie-consent record the server already holds.
    clearedOnLogout: false,
  },
  {
    key: 'symbian_consent_outbox',
    storage: 'localStorage',
    category: 'essential',
    name: 'Unsent consent records',
    purpose:
      'Holds consent choices that could not reach our servers — for example if your connection dropped — so they are sent as soon as we can rather than being lost.',
    personalData: true,
    retention: 'Until the record is successfully sent.',
    // NOT cleared on logout: a queued record is evidence of a decision the
    // patient already made. Discarding it at sign-out would silently destroy
    // consent evidence that has not yet reached the ledger.
    clearedOnLogout: false,
  },

  // ── Functional ───────────────────────────────────────────────────────────
  {
    key: 'themeMode',
    storage: 'localStorage',
    category: 'functional',
    name: 'Light or dark mode',
    purpose: 'Remembers your appearance preference.',
    personalData: false,
    retention: 'Until you clear your browser data.',
    clearedOnLogout: false,
  },
  {
    key: 'symbian_selected_region',
    storage: 'localStorage',
    category: 'functional',
    name: 'Your region',
    purpose:
      'Remembers whether you are using the India or Global version, which determines the consent terms shown to you.',
    personalData: false,
    retention: 'Until you clear your browser data.',
    clearedOnLogout: false,
  },
  {
    key: 'symbian_sidebar_collapsed',
    storage: 'localStorage',
    category: 'functional',
    name: 'Sidebar layout',
    purpose: 'Remembers whether you collapsed the navigation menu.',
    personalData: false,
    retention: 'Until you clear your browser data.',
    clearedOnLogout: false,
  },
  {
    key: 'symbian_search_recents',
    storage: 'localStorage',
    category: 'functional',
    name: 'Recent searches',
    purpose:
      'Shows your last five searches when you open the search box. For clinical staff these can include patient names, so it is cleared when you sign out.',
    personalData: true,
    retention: 'Last 5 searches. Cleared on sign-out.',
    clearedOnLogout: true,
  },
];

export const CLIENT_STORAGE_POLICY_VERSION = '2026.08.1';

/** Consent expiry. Re-ask annually, and immediately on a version bump. */
export const COOKIE_CONSENT_MAX_AGE_DAYS = 365;

export const getItemsForCategory = (category: CookieCategory): StoredItem[] =>
  STORED_ITEMS.filter((i) => i.category === category);

/**
 * Keys that must be purged on sign-out.
 *
 * Sign-out previously removed only `stepper_completed`, leaving payment status
 * and staff search history (containing patient names) on shared machines.
 */
export const KEYS_CLEARED_ON_LOGOUT = STORED_ITEMS
  .filter((i) => i.clearedOnLogout && !i.key.includes('*'))
  .map((i) => i.key);
